import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../ui';
import { MarkEmailUnread } from '../ui/icons';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/authService';
import SEO from '../common/SEO';
import { Warning } from '../ui/icons';
import type { AccessDecision } from '../../context/authGating';

interface AccountStatusGateProps {
  /** Which non-`allow` gate to render. */
  decision: Exclude<AccessDecision, 'allow'>;
  /** Raw company status — used to title the blocked screen accurately. */
  status?: string;
}

const cardWrap = (children: React.ReactNode) => (
  <>
    <div className="fixed inset-0 z-0 bg-gradient-to-b from-canvas via-surface-2 to-canvas" />
    <div className="relative z-10 min-h-screen flex items-center justify-center px-4 sm:px-6">
      <div className="w-full max-w-[440px] rounded-card border border-primary/20 bg-surface/95 p-6 sm:p-8 shadow-card">
        {children}
      </div>
    </div>
  </>
);

/**
 * Central screen shown when an authenticated session must NOT enter the app
 * shell (unverified email / pending approval / blocked company). The decision is
 * computed by `evaluateAccess`; this component only renders the matching state.
 * The verify-email variant keeps the resend action reachable and offers a
 * re-check so the user can continue once they have confirmed their email.
 */
const AccountStatusGate: React.FC<AccountStatusGateProps> = ({ decision, status }) => {
  const { t } = useTranslation();
  const { currentUser, logout, refreshEmailVerification, refreshUserData } = useAuth();
  const email = currentUser?.email || '';

  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [loggingOut, setLoggingOut] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      // Re-fetches the company document; while it loads, AuthContext flips
      // `loading` true so AppLayout shows the spinner, then re-evaluates the gate.
      await refreshUserData();
    } finally {
      setRetrying(false);
    }
  };

  const handleResend = async () => {
    if (!email || resendStatus === 'sending') return;
    setResendStatus('sending');
    try {
      await authService.resendVerificationEmail(email);
      setResendStatus('sent');
    } catch {
      setResendStatus('error');
    }
  };

  const handleRecheck = async () => {
    setRechecking(true);
    try {
      await refreshEmailVerification();
    } finally {
      setRechecking(false);
    }
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      setLoggingOut(false);
    }
  };

  const logoutButton = (
    <button
      disabled={loggingOut}
      onClick={handleLogout}
      className="w-full py-2 px-4 rounded-xl font-medium text-sm text-muted hover:text-primary transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
    >
      {loggingOut && <Spinner size={16} className="text-primary" />}
      {t('auth.logout')}
    </button>
  );

  if (decision === 'verify-email') {
    return (
      <>
        <SEO title={t('auth.verify_email_title')} noIndex={true} />
        {cardWrap(
          <div className="flex flex-col items-center text-center gap-5">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
              <MarkEmailUnread size={28} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-content mb-1">{t('auth.verify_email_title')}</h2>
              <p className="text-sm text-muted">{t('auth.verify_email_warning')}</p>
              {email && <p className="text-sm text-muted mt-1">{email}</p>}
            </div>

            {resendStatus === 'sent' && (
              <div className="w-full rounded-xl border border-success/30 bg-success/10 px-4 py-3 text-sm text-success">
                {t('auth.resend_email_success')}
              </div>
            )}
            {resendStatus === 'error' && (
              <div className="w-full rounded-xl bg-primary px-4 py-3 text-sm text-white">
                {t('auth.resend_email_error')}
              </div>
            )}

            <button
              disabled={rechecking}
              onClick={handleRecheck}
              className="w-full py-3 px-4 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-primary-dark transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed shadow-btn-primary"
            >
              {rechecking && <Spinner size={16} className="text-white" />}
              {t('auth.verify_email_recheck')}
            </button>

            <button
              disabled={resendStatus === 'sending' || resendStatus === 'sent'}
              onClick={handleResend}
              className="w-full py-3 px-4 rounded-xl border border-primary text-primary font-semibold text-sm hover:bg-primary/10 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {resendStatus === 'sending' && <Spinner size={16} className="text-primary" />}
              {resendStatus === 'sending' ? t('auth.resend_email_sending') : t('auth.resend_email_button')}
            </button>

            {logoutButton}
          </div>
        )}
      </>
    );
  }

  if (decision === 'account-error') {
    return (
      <>
        <SEO title={t('companies.account_error_title')} noIndex={true} />
        {cardWrap(
          <div className="flex flex-col items-center text-center gap-5">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
              <Warning size={28} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-content mb-1">{t('companies.account_error_title')}</h2>
              <p className="text-sm text-muted">{t('companies.account_error_message')}</p>
            </div>

            <button
              disabled={retrying}
              onClick={handleRetry}
              className="w-full py-3 px-4 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-primary-dark transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed shadow-btn-primary"
            >
              {retrying && <Spinner size={16} className="text-white" />}
              {t('companies.account_error_retry')}
            </button>

            <p className="text-sm text-muted">{t('companies.rejected_contact')}</p>
            {logoutButton}
          </div>
        )}
      </>
    );
  }

  // pending / blocked — informational screen with a sign-out action.
  const isPending = decision === 'pending';
  const titleKey = isPending
    ? 'companies.status.pending_verification'
    : `companies.status.${status === 'rejected' ? 'rejected' : 'suspended'}`;
  const messageKey = isPending
    ? 'companies.pending_message'
    : status === 'rejected'
      ? 'companies.rejected_message'
      : 'companies.suspended_message';

  return (
    <>
      <SEO title={t(titleKey)} noIndex={true} />
      {cardWrap(
        <div className="flex flex-col items-center text-center gap-5">
          <div className="w-full p-4 rounded-xl border border-primary/30 bg-primary/10">
            <h2 className="text-base font-bold mb-2 text-content">{t(titleKey)}</h2>
            <p className="text-sm text-muted">{t(messageKey)}</p>
          </div>
          <p className="text-sm text-muted">{t('companies.rejected_contact')}</p>
          {logoutButton}
        </div>
      )}
    </>
  );
};

export default AccountStatusGate;
